import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const NicknameModal = ({ isOpen, onClose, onConfirm, friendName, currentNickname }) => {
  const { t } = useTranslation('chat');
  const [nickname, setNickname] = useState('');

  // Update nickname when modal opens or currentNickname changes
  useEffect(() => {
    if (isOpen) {
      setNickname(currentNickname || '');
    }
  }, [isOpen, currentNickname]);

  const handleConfirm = () => {
    // Trim whitespace, if empty pass null to remove nickname
    const trimmedNickname = nickname.trim();
    onConfirm(trimmedNickname.length > 0 ? trimmedNickname : null);
    onClose();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleConfirm();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100000]"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-[100001] flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[var(--color-bg-base)] dark:bg-[var(--color-bg-elevated)] rounded-2xl shadow-2xl w-full max-w-md border border-white/10 dark:border-white/10">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-white/10 dark:border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h2 className="text-xl font-bold text-[var(--color-text-primary)] dark:text-white">
                    {t('nicknameModal.title')}
                  </h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#1e2d47] text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                <p className="text-[var(--color-text-secondary)] dark:text-gray-300">
                  {t('nicknameModal.descriptionPrefix')} <span className="font-semibold">{friendName}</span>{t('nicknameModal.descriptionSuffix')}
                </p>
                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300 mb-2">
                    {t('nicknameModal.label')}
                  </label>
                  <input
                    type="text"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={friendName}
                    maxLength={50}
                    className="w-full px-4 py-3 rounded-xl border-2 border-white/10 dark:border-white/10 bg-[var(--color-bg-base)] dark:bg-[#161f31]/60 text-[var(--color-text-primary)] dark:text-white placeholder-[var(--color-text-muted)] dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50 focus:border-transparent transition-all"
                    autoFocus
                  />
                  <p className="mt-2 text-xs text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)]">
                    {t('nicknameModal.hint')}
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10 dark:border-white/10">
                <button
                  onClick={onClose}
                  className="px-4 py-2 rounded-xl bg-[var(--color-bg-elevated)] dark:bg-[#161f31] hover:bg-[#24354f] dark:hover:bg-[#24354f] text-[var(--color-text-secondary)] dark:text-gray-300 transition-colors"
                >
                  {t('common:cancel')}
                </button>
                <button
                  onClick={handleConfirm}
                  className="px-4 py-2 rounded-xl bg-[#2563eb] hover:bg-[#3d8ef0] text-white transition-colors"
                >
                  {t('common:save')}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default NicknameModal;
